import { Menu, type MenuEntry } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** One account-menu entry. A disabled entry reports nothing and leaves the menu open. */
export type UserMenuEntry = MenuEntry

/**
 * Props for {@link UserMenu}.
 *
 * @property label - The trigger's text and accessible name (e.g. the user's name).
 * @property entries - Account actions, in render order.
 * @property onAction - Reports the activated entry's `id`.
 * @property className - Extra classes merged onto the trigger.
 */
export interface UserMenuProps {
	label: string
	entries: UserMenuEntry[]
	onAction?: (id: string) => void
	className?: string
}

/**
 * The header account menu.
 *
 * @remarks
 * - **Built on the `@today/ui-primitives` `Menu`** (which wraps React Aria's
 *   `MenuTrigger`/`Menu`/`MenuItem`/`Popover` with `ds-*` tokens), NOT the local
 *   shadcn `@/components/ui/dropdown-menu`: that component is styled with
 *   unthemed `bg-popover`/`bg-accent` classes outside the `ds-*` system and is
 *   not scanned by the brand-source guard (red-team RT-FRO-05). REQ-004.
 * - **Accessibility:** the entry list is absent from the accessibility tree
 *   until the trigger is activated; it then exposes `role="menu"` with
 *   `role="menuitem"` entries, Arrow-key navigation and Escape to dismiss.
 * - **Selection is the consumer's:** the activated entry's `id` is reported
 *   through `onAction`; disabled entries report nothing.
 */
export function UserMenu({ label, entries, onAction, className }: UserMenuProps): ReactElement {
	return <Menu label={label} entries={entries} onAction={(key) => onAction?.(String(key))} className={className} />
}
